import { TPricingIcon } from './components/PricingIcon'

export interface IPricingFeatureSegment {
    text: string
    bold?: boolean
}

export interface IPricingFeature {
    segments: IPricingFeatureSegment[]
    tooltip: string
}

export interface IEnterpriseFeature {
    icon: TPricingIcon
    title: string
    description: string
}

const bold = (text: string): IPricingFeatureSegment => ({ text, bold: true })
const plain = (text: string): IPricingFeatureSegment => ({ text })

// Bold parts follow the Figma design
export const growthFeatures: IPricingFeature[] = [
    {
        segments: [bold('Unlimited LinkedIn/Email campaigns'), plain(' + '), bold('AI LinkedIn Inbox')],
        tooltip: 'Run as many LinkedIn and email sequences as you need, with no caps. The AI Inbox sorts, labels, and prioritizes replies so you never miss a hot conversation.'
    },
    {
        segments: [bold('500M people database'), plain(' + '), bold('Email, Phone finder'), plain(' (500 credits)')],
        tooltip: 'Search 500M+ B2B profiles and find verified emails and phone numbers. Includes 500 enrichment credits to get started.'
    },
    {
        segments: [bold('LinkedIn CRM + Shared Networks')],
        tooltip: "Organize every connection with tags, notes, and tasks in one CRM. Pool your team's LinkedIn networks so everyone can see who knows whom."
    },
    {
        segments: [bold('Warm Intro AI:'), plain(' find the warmest intro path into prospects')],
        tooltip: "Ask AI to map your team's shared network to find the strongest mutual connection to any prospect. Turn cold outreach into warm introductions."
    },
    {
        segments: [bold('Buying signals:'), plain(' job and company changes, custom feeds')],
        tooltip: 'Get alerted when contacts change jobs or companies hit key moments. Build custom post feeds to track when they post, limited to 500 tracked profiles per user.'
    },
    {
        segments: [bold('MCP/API:'), plain(' Works with Claude, ChatGPT, HubSpot, Zapier')],
        tooltip: 'Connect LeadDelta to Claude, ChatGPT, HubSpot, Zapier, and your own tools. Let AI agents work with your network data and keep your stack in sync.'
    }
]

export const doneForYou = {
    title: 'Done-for-you',
    description: 'You close deals, we handle the rest.',
    price: 'Custom service',
    features: [
        {
            segments: [plain('GTM strategy setup')],
            tooltip: 'We define your ICP, messaging, and outbound playbook with you before anything goes live.'
        },
        {
            segments: [plain('Dedicated GTM engineer')],
            tooltip: 'Option to hire an expert who builds, runs, and optimizes your outbound engine as an extension of your team.'
        },
        {
            segments: [plain('Custom AI workflow builder')],
            tooltip: 'We design AI workflows around your process, from prospect research and enrichment to personalized messaging and routing.'
        },
        {
            segments: [plain('Campaign setup/Multichannel outreach')],
            tooltip: 'We build and launch coordinated LinkedIn and email campaigns, including targeting, copy, and sequencing.'
        },
        {
            segments: [plain('Weekly or Monthly reporting')],
            tooltip: 'Clear custom reports we agree on, choose between custom options.'
        },
        {
            segments: [plain('Minimum 3-month commitment')],
            tooltip: 'Outbound compounds over time. Three months gives us room to test, learn, and scale what works.'
        }
    ] as IPricingFeature[]
}

export const enterprise = {
    title: 'Enterprise',
    description: "Built for 100–10,000 people organizations that want to standardize relationship selling across their enterprise. Enterprise plans are customized: during scoping we'll align on seats, credits, integrations, and security needs. SOC 2 and ISO 27001 certifications are in progress.",
    quote: '"LeadDelta workspaces turn conversations into opportunities. We can act quickly and as a team."',
    quoteAuthor: 'Global Team, HarCourt (USA, France, UK)',
    features: [
        { icon: 'rocket', title: 'Deploy at scale', description: 'Roll out across teams and regions' },
        { icon: 'users', title: 'Get a true partner', description: 'Dedicated Customer Success for proactive coaching and QBRs' },
        { icon: 'sparkles', title: 'Launch smoothly', description: 'Custom onboarding & team training tailored to your playbooks' },
        { icon: 'layers', title: 'Influence the roadmap', description: 'Product Advisory Board seat' },
        { icon: 'shield', title: 'Custom security', description: 'SSO (SAML & OIDC) for secure, compliant sign-in' },
        { icon: 'settings', title: 'Fit your stack', description: 'Custom integrations to connect your CRM, data, and internal tools' }
    ] as IEnterpriseFeature[]
}
